import { AuditOutlined, DeleteOutlined, LinkOutlined } from '@ant-design/icons';
import { RouteContext } from '@ant-design/pro-components';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Flex, Space, Tooltip } from 'antd';
import { getReviewTasks } from '@/services/ant-design-pro/api';
import packageJson from '../../../package.json';
import { Link } from 'react-router';

/** ProLayout 内部折叠状态，含 breakpoint 初次收起，不依赖父组件 onCollapse */
const useSiderCollapsed = () => !!useContext(RouteContext).collapsed;

/**
 * 单个链接组件，自动根据当前语言显示对应文案。
 */
const LinkItem: React.FC<{ id: string; href: string; external?: boolean }> = ({
  id,
  href,
  external,
}) => {
  const { t } = useTranslation();
  const collapsed = useSiderCollapsed();

  return (
    <Tooltip title={collapsed ? t(id) : null} placement="right">
      <a
        key={id}
        href={href}
        target={external ? '_blank' : undefined}
        rel={external ? 'noopener noreferrer' : undefined}
      >
        <Space size={8}>
          <LinkOutlined />
          {collapsed ? null : <span>{t(id)}</span>}
        </Space>
      </a>
    </Tooltip>
  );
};

/**
 * 评审任务链接
 * @returns
 */
const ReviewTaskLinkItem: React.FC = () => {
  const { t } = useTranslation();
  const collapsed = useSiderCollapsed();

  const [count, setCount] = useState(0);

  const fetchReviewTaskCount = async () => {
    const response = await getReviewTasks({ current: 1, pageSize: 1 });
    if (response.success) {
      setCount(response.total);
      return;
    }
    return 0;
  };

  useEffect(() => {
    fetchReviewTaskCount();

    const timer = setInterval(fetchReviewTaskCount, 1000 * 60);

    return () => {
      clearInterval(timer);
    };
  }, []);

  return collapsed ? (
    <Tooltip title={t('pages.layout.reviewTasks')} placement="right">
      <Badge size="small" dot={count > 0}>
        <a key="review-task" href="/review-tasks" target="_blank" rel="noopener noreferrer">
          <AuditOutlined />
        </a>
      </Badge>
    </Tooltip>
  ) : (
    <a key="review-task" href="/review-tasks" target="_blank" rel="noopener noreferrer">
      <Flex justify="space-between" align="center">
        <Space size={8} className="flex-1">
          <AuditOutlined />
          <span>{t('pages.layout.reviewTasks')}</span>
        </Space>

        <Badge count={count} />
      </Flex>
    </a>
  );
};

/**
 * 发布日志链接
 * @returns
 */
const ChangelogLinkItem: React.FC = () => {
  const collapsed = useSiderCollapsed();
  return (
    <Tooltip title={collapsed ? `v${packageJson.version}` : null} placement="right">
      <a key="changelog" href="/changelog" target="_blank" rel="noopener noreferrer">
        <span>v{collapsed ? null : packageJson.version}</span>
      </a>
    </Tooltip>
  );
};

/**
 * 回收站链接
 * @returns
 */
const RecycleBinLinkItem: React.FC = () => {
  const { t } = useTranslation();
  const collapsed = useSiderCollapsed();
  return (
    <Tooltip title={collapsed ? t('menu.recycle-bin') : null} placement="right">
      <Link to="/docs/recycle-bin">
        <Space size={8}>
          <DeleteOutlined />
          {collapsed ? null : <span>{t('menu.recycle-bin')}</span>}
        </Space>
      </Link>
    </Tooltip>
  );
};

/**
 * 工厂函数：返回给 ProLayout 的链接数组。
 */
export const createLayoutLinks = () => [
  <ReviewTaskLinkItem key="review-task" />,
  <RecycleBinLinkItem key="recycle-bin" />,
  <LinkItem
    key="product-library"
    id="pages.layout.productLibrary"
    href="/product-library"
    external
  />,
  <LinkItem key="process-docs" id="pages.layout.processDocsPreview" href="/processdocs" external />,
  <LinkItem key="system-manual" id="pages.layout.systemManual" href="/help" external />,
  <ChangelogLinkItem key="changelog" />,
];

export default createLayoutLinks;
